import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { MeetingDto } from '@interfaces/meetings/responses/meeting.response';
import { SharedModule } from '@src/app/shared/shared.module';
import { formatarMarcaDeTempo } from '@features/dashboard/meetings/components/meeting-presenter';
import {
  avisoDeGravacaoParcial,
  explicacaoSemGravacao,
  processamentoDaGravacao,
} from '@features/dashboard/meetings/pages/meeting-detail/meeting-detail-state';

/** Onde tirar o quadro da capa: 15% da reunião, no máximo 1 min (o começo costuma ser tela preta ou lobby). */
const CAPA_FRACAO = 0.15;
const CAPA_MAXIMO_S = 60;

/** Situações de reunião que ainda não aconteceu (a cancelada tem estado próprio). */
const AINDA_NAO_ACONTECEU: readonly string[] = ['Scheduled', 'BotCancelled'];

/**
 * O lugar do vídeo na tela de detalhe: o player (16:9, sem faixa preta em volta) ou, sem vídeo, o
 * estado que explica por quê: processando (com as etapas), reunião futura, cancelada, remarcada ou
 * terminada sem gravação, com o motivo que a Recall deu (`noRecordingReason`).
 *
 * Quem pede o link do vídeo é a tela; aqui só ficam os eventos do `<video>` e o `irPara`, que a
 * transcrição e os capítulos usam para levar o vídeo a um ponto.
 */
@Component({
  selector: 'app-meeting-player',
  standalone: true,
  imports: [SharedModule],
  templateUrl: './meeting-player.component.html',
  styleUrl: './meeting-player.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MeetingPlayerComponent {
  readonly meeting = input.required<MeetingDto>();
  readonly videoUrl = input<string | null>(null);
  readonly videoErro = input<string | null>(null);

  /** Segundo atual, a cada `timeupdate`/`seeked`. */
  readonly tempo = output<number>();
  /** O `<video>` recusou o link (expirou ou a rede caiu), com o ponto em que estava. */
  readonly falhou = output<number>();
  readonly carregarDeNovo = output<void>();
  /** "Veja a linha do tempo" no aviso de gravação parcial (card 3244): o pai abre a aba Detalhes. */
  readonly verLinhaDoTempo = output<void>();

  private readonly player = viewChild<ElementRef<HTMLVideoElement>>('player');
  /** Onde o vídeo estava quando o link expirou, para voltar ao mesmo ponto. */
  private retomarEm = 0;

  /**
   * Capa antes do primeiro play: o `<video>` mostra um quadro do meio da reunião (o primeiro quadro
   * costuma ser preto), com véu escuro, o logo do Cognito e o botão de play. O play começa do 00:00.
   */
  protected readonly capa = signal(true);
  protected readonly duracaoDoVideo = signal<string | null>(null);

  protected readonly processamento = computed(() => processamentoDaGravacao(this.meeting()));
  protected readonly semGravacao = computed(() => explicacaoSemGravacao(this.meeting()));
  protected readonly gravacaoParcial = computed(() => avisoDeGravacaoParcial(this.meeting()));
  protected readonly aindaNaoAconteceu = computed(() =>
    AINDA_NAO_ACONTECEU.includes(this.meeting().status)
  );

  /** Leva o vídeo para o segundo e toca (clique na transcrição, num capítulo ou num trecho do Cog). */
  irPara(segundos: number): void {
    const player = this.player()?.nativeElement;
    if (!player) return;
    this.capa.set(false);
    player.currentTime = segundos;
    this.tempo.emit(segundos);
    void player.play().catch(() => undefined);
    player.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  /** Guarda o ponto antes de pedir um link novo. */
  retomarNo(segundos: number): void {
    // Com a capa, o vídeo está parado no quadro dela, não onde a pessoa parou: o link novo volta com capa.
    this.retomarEm = this.capa() ? 0 : segundos;
  }

  protected aoTocar(): void {
    // Com a capa, o vídeo está parado no quadro dela: a transcrição continua no começo.
    if (this.capa()) return;
    this.tempo.emit(this.player()?.nativeElement.currentTime ?? 0);
  }

  protected aoCarregar(): void {
    const player = this.player()?.nativeElement;
    if (!player) return;
    if (Number.isFinite(player.duration)) {
      this.duracaoDoVideo.set(formatarMarcaDeTempo(player.duration));
    }
    if (this.retomarEm > 0) {
      // Link novo depois de expirar: volta ao ponto, sem capa.
      this.capa.set(false);
      player.currentTime = this.retomarEm;
      this.retomarEm = 0;
      return;
    }
    if (this.capa() && Number.isFinite(player.duration) && player.duration > 0) {
      player.currentTime = Math.min(player.duration * CAPA_FRACAO, CAPA_MAXIMO_S);
    }
  }

  /** Play pela capa: tira a capa e começa do início. */
  protected assistir(): void {
    const player = this.player()?.nativeElement;
    if (!player) return;
    this.capa.set(false);
    player.currentTime = 0;
    this.tempo.emit(0);
    void player.play().catch(() => undefined);
  }

  /** Play pelos controles nativos (teclado, por exemplo) com a capa ainda na tela. */
  protected aoComecar(): void {
    if (this.capa()) this.assistir();
  }

  protected aoFalhar(): void {
    this.falhou.emit(this.player()?.nativeElement.currentTime ?? 0);
  }
}
